import React, { useState } from "react";
import Logo from "../../assets/logo.png";
import "./Sidebar.css";
import { UserButton } from "@clerk/clerk-react";

// COMPONENT: Left side navigation menu
const Sidebar = ({
  activeSection,
  setActiveSection,
  onTimeRangeChange,
  selectedTimeRange,
  isMobileOpen = false,
  onCloseSidebar,
}) => {
  // STATE: Which menu is expanded (collapsed by default)
  const [expandedMenu, setExpandedMenu] = useState(null);
  // STATE: Month and year dropdowns
  const [expandedMonth, setExpandedMonth] = useState(false);
  const [expandedYear, setExpandedYear] = useState(false);
  // STATE: Custom date picker
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [customEndDate, setCustomEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // MENU ITEMS: The 4 main pages
  const mainSections = [
    { id: "overview", label: "Executive Summary", icon: "fas fa-chart-bar" },
    { id: "locations", label: "Locations", icon: "fas fa-map-pin" },
    { id: "devices", label: "All Devices", icon: "fas fa-mobile-alt" },
    { id: "reports", label: "Reports & Analytics", icon: "fas fa-chart-line" },
  ];

  // TIME RANGE OPTIONS: How far back to show data (added "last24hours")
  const timeRanges = [
    { id: "last24hours", label: "Last 24 Hours", icon: "fas fa-clock" },
    { id: "today", label: "Today" },
    { id: "yesterday", label: "Yesterday" },
    { id: "week", label: "This Week" },
    { id: "last7days", label: "Last 7 Days" },
    { id: "last14days", label: "Last 14 Days" },
    { id: "month", label: "Last 30 Days" },
    { id: "quarter", label: "This Quarter" },
  ];

  // QUICK METRICS: Show in sidebar
  const metrics = [
    { id: "utilization", label: "Utilization", icon: "fas fa-bolt" },
    { id: "uptime", label: "Uptime", icon: "fas fa-check-circle" },
    { id: "rentals", label: "Rentals", icon: "fas fa-key" },
    { id: "revenue", label: "Revenue", icon: "fas fa-dollar-sign" },
    {
      id: "offline-incidents",
      label: "Offline Incidents",
      icon: "fas fa-exclamation-triangle",
    },
  ];

  // GENERATE MONTH OPTIONS: Last 12 months, recent first
  const generateMonths = () => {
    const months = [];
    const now = new Date();
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const monthNum = String(date.getMonth() + 1).padStart(2, "0");
      months.push({
        label: `${monthName} ${year}`,
        value: `${year}-${monthNum}`,
      });
    }
    return months;
  };

  // GENERATE YEAR OPTIONS: Last 5 years, recent first
  const generateYears = () => {
    const years = [];
    const now = new Date();
    for (let i = 0; i < 5; i++) {
      const year = now.getFullYear() - i;
      years.push({ label: year.toString(), value: year.toString() });
    }
    return years;
  };

  const months = generateMonths();
  const years = generateYears();

  // COMPUTE LABELS FOR HEADERS
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthNum = String(now.getMonth() + 1).padStart(2, "0");
  const currentMonthValue = `${currentYear}-${currentMonthNum}`;
  const currentYearValue = currentYear.toString();

  let monthLabel =
    months.find((m) => `month-${m.value}` === selectedTimeRange)?.label ||
    "This Month";
  if (selectedTimeRange === `month-${currentMonthValue}`) {
    monthLabel = "This Month";
  }

  let yearLabel =
    years.find((y) => `year-${y.value}` === selectedTimeRange)?.label ||
    "This Year";
  if (selectedTimeRange === `year-${currentYearValue}`) {
    yearLabel = "This Year";
  }

  // HELPER: Get last 24 hours from current time
  const getLast24HoursRange = () => {
    const end = new Date(); // Current time
    const start = new Date(end);
    start.setHours(start.getHours() - 24); // Go back 24 hours
    return { start, end };
  };

  // HELPER: Get start of today (midnight)
  const getTodayStart = () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return start;
  };

  // HELPER: Yesterday full day
  const getYesterdayRange = () => {
    const end = new Date();
    end.setDate(end.getDate() - 1);
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  };

  // HELPER: Start of current week (Monday)
  const getWeekStart = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday
    const start = new Date(now);
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getLast7DaysStart = () => {
    const start = new Date();
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getLast14DaysStart = () => {
    const start = new Date();
    start.setDate(start.getDate() - 13);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getMonthStart = () => {
    const start = new Date();
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getQuarterStart = () => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const start = new Date(now);
    start.setMonth(quarter * 3);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    if (window.innerWidth <= 768 && onCloseSidebar) {
      onCloseSidebar();
    }
  };

  const handleTimeRangeClick = (range) => {
    if (!onTimeRangeChange) return;
    let start, end = new Date();

    switch (range.id) {
      case "last24hours": {
        const range24h = getLast24HoursRange();
        start = range24h.start;
        end = range24h.end;
        break;
      }
      case "today":
        start = getTodayStart();
        break;
      case "yesterday": {
        const { start: yStart, end: yEnd } = getYesterdayRange();
        start = yStart;
        end = yEnd;
        break;
      }
      case "week":
        start = getWeekStart();
        break;
      case "last7days":
        start = getLast7DaysStart();
        break;
      case "last14days":
        start = getLast14DaysStart();
        break;
      case "month":
        start = getMonthStart();
        break;
      case "quarter":
        start = getQuarterStart();
        break;
      default:
        return;
    }

    onTimeRangeChange({ start, end }, range.id);
    if (window.innerWidth <= 768 && onCloseSidebar) {
      onCloseSidebar();
    }
  };

  const handleMonthSelect = (month) => {
    const [year, mon] = month.value.split("-");
    const start = new Date(parseInt(year), parseInt(mon) - 1, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(parseInt(year), parseInt(mon), 0);
    end.setHours(23, 59, 59, 999);
    setExpandedMonth(false);
    onTimeRangeChange({ start, end }, `month-${month.value}`);
  };

  const handleYearSelect = (yearObj) => {
    const year = parseInt(yearObj.value);
    const start = new Date(year, 0, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(year, 11, 31);
    end.setHours(23, 59, 59, 999);
    setExpandedYear(false);
    onTimeRangeChange({ start, end }, `year-${yearObj.value}`);
  };

  const selectCurrentMonth = () => handleMonthSelect(months[0]);
  const selectCurrentYear = () => handleYearSelect(years[0]);

  const handleCustomApply = () => {
    const startDateObj = new Date(customStartDate);
    startDateObj.setHours(0, 0, 0, 0);
    let endDateObj = new Date(customEndDate);
    endDateObj.setHours(23, 59, 59, 999);
    if (endDateObj > new Date()) endDateObj = new Date();
    if (startDateObj > endDateObj) {
      alert("Start date must be before or equal to end date.");
      return;
    }
    onTimeRangeChange({ start: startDateObj, end: endDateObj }, "custom");
    setShowCustomPicker(false);
  };

  const toggleMenu = (menuId) => {
    setExpandedMenu(expandedMenu === menuId ? null : menuId);
  };

  const isMonthActive = selectedTimeRange.startsWith("month-");
  const isYearActive = selectedTimeRange.startsWith("year-");

  // Format time for display in the sidebar
  const formatTimeRangeForDisplay = () => {
    if (selectedTimeRange === "last24hours") {
      const now = new Date();
      const twentyFourHoursAgo = new Date(now);
      twentyFourHoursAgo.setHours(now.getHours() - 24);
      
      const formatTime = (date) => {
        return date.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      };
      
      return `${formatTime(twentyFourHoursAgo)} - ${formatTime(now)}`;
    }
    return null;
  };

  const timeRangeDisplay = formatTimeRangeForDisplay();

  return (
    <aside className={`sidebar ${isMobileOpen ? "mobile-open" : ""}`}>
      {/* MOBILE CLOSE BUTTON */}
      {window.innerWidth <= 768 && (
        <div className="sidebar-mobile-header">
          <button className="mobile-close-btn" onClick={onCloseSidebar}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {/* HEADER */}
      <div className="sidebar-header">
        <div className="logo-container">
          <div className="logo-icon">
            <img src={Logo} alt="Company Logo" className="logo" />
          </div>
          <div className="logo-text">
            <h2>Dashboard</h2>
            <p className="sidebar-tagline">Fleet Intelligence</p>
          </div>
        </div>
      </div>

      {/* CLERK USER BUTTON - Enhanced Profile Dropdown */}
      <div className="user-profile-dropdown">
        <div className="clerk-user-button">
          <UserButton
            afterSignOutUrl="/"
            showName={true}
            userProfileMode="navigation"
            userProfileUrl="/user-profile"
            appearance={{
              elements: {
                userButtonBox: "cl-userButtonBox",
                userButtonTrigger: "cl-userButtonTrigger",
                userButtonAvatarBox: "cl-userButtonAvatarBox", 
                userButtonOuterIdentifier: "cl-userButtonOuterIdentifier",
                dropdownMenu: "cl-dropdownMenu",
                dropdownMenuActionItem: "cl-dropdownMenuActionItem",
                userPreview: "cl-userPreview",
                userPreviewMainIdentifier: "cl-userPreviewMainIdentifier",
                userPreviewSecondaryIdentifier: "cl-userPreviewSecondaryIdentifier"
              }
            }}
          />
        </div>
      </div>

      {/* SECTION 1: Main Navigation */}
      <div className="sidebar-section">
        <h3 className="section-title">Navigation</h3>
        <ul className="sidebar-list">
          {mainSections.map((section) => (
            <li key={section.id}>
              <button
                className={`sidebar-item ${activeSection === section.id ? "active" : ""}`}
                onClick={() => handleSectionClick(section.id)}
              >
                <i className={section.icon}></i>
                <span className="item-text">{section.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* SECTION 2: Time Range Filters */}
      <div className="sidebar-section">
        <h3 className="section-title">Time Range</h3>
        <div className="time-filters">
          {timeRanges.map((range) => (
            <button
              key={range.id}
              className={`time-btn ${selectedTimeRange === range.id ? "active" : ""} ${
                range.id === "last24hours" ? "with-icon" : ""
              }`}
              onClick={() => handleTimeRangeClick(range)}
              title={range.id === "last24hours" ? "Last 24 hours from now" : range.label}
            >
              {range.id === "last24hours" && <i className="fas fa-clock"></i>}
              {range.label}
            </button>
          ))}
        </div>

        {/* Show current time range for last 24 hours */}
        {selectedTimeRange === "last24hours" && timeRangeDisplay && (
          <div className="time-range-display">
            <span className="time-range-label">
              <i className="fas fa-history"></i>
              {timeRangeDisplay}
            </span>
          </div>
        )}

        {/* Month Section */}
        <div className={`month-section ${isMonthActive ? "active" : ""}`}>
          <div className="month-header">
            <button className="month-label" onClick={selectCurrentMonth}>
              {monthLabel}
            </button>
            <button
              className="collapse-btn"
              onClick={() => setExpandedMonth(!expandedMonth)}
            >
              {expandedMonth ? "▼" : "▶"}
            </button>
          </div>
          {expandedMonth && (
            <ul className="dropdown-list">
              {months.map((month) => (
                <li key={month.value}>
                  <button
                    className={`time-btn dropdown-btn ${
                      selectedTimeRange === `month-${month.value}` ? "active" : ""
                    }`}
                    onClick={() => handleMonthSelect(month)}
                  >
                    {month.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Year Section */}
        <div className={`year-section ${isYearActive ? "active" : ""}`}>
          <div className="year-header">
            <button className="year-label" onClick={selectCurrentYear}>
              {yearLabel}
            </button>
            <button
              className="collapse-btn"
              onClick={() => setExpandedYear(!expandedYear)}
            >
              {expandedYear ? "▼" : "▶"}
            </button>
          </div>
          {expandedYear && (
            <ul className="dropdown-list">
              {years.map((yearObj) => (
                <li key={yearObj.value}>
                  <button
                    className={`time-btn dropdown-btn ${
                      selectedTimeRange === `year-${yearObj.value}` ? "active" : ""
                    }`}
                    onClick={() => handleYearSelect(yearObj)}
                  >
                    {yearObj.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Custom Date Range */}
        <div className="custom-date-section">
          <button
            className={`time-btn ${selectedTimeRange === "custom" ? "active" : ""}`}
            onClick={() => setShowCustomPicker(!showCustomPicker)}
          >
            Custom Range
          </button>
          {showCustomPicker && (
            <div className="date-picker-popup">
              <div className="date-inputs">
                <div className="date-group">
                  <label>From:</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div className="date-group">
                  <label>To:</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
                <div className="date-buttons">
                  <button className="apply-btn" onClick={handleCustomApply}>
                    Apply
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => setShowCustomPicker(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 3: Quick Metrics */}
      <div className="sidebar-section">
        <div className="metrics-header">
          <h3 className="section-title">Quick Metrics</h3>
          <button
            className="collapse-btn"
            onClick={() => toggleMenu("metrics")}
          >
            {expandedMenu === "metrics" ? "▼" : "▶"}
          </button>
        </div>
        {expandedMenu === "metrics" && (
          <ul className="metrics-list">
            {metrics.map((metric) => (
              <li key={metric.id} className="metric-item">
                <i className={metric.icon}></i>
                <span className="metric-text">{metric.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* FOOTER */}
      <div className="sidebar-footer">
        <div className="footer-stat">
          <span className="stat-label">Last Updated:</span>
          <span className="stat-value">{new Date().toLocaleTimeString()}</span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;